import { useEffect, useLayoutEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Grid } from "@material-ui/core";
import { FieldSetBox } from "../../fieldSetBox";
import { StatementOfLimitingConditions } from "../stateOfLimitingConditions";
import { CompMap, MapLegend } from "../../mapComponents";
import { InternalValuationReviewStore } from "../../../stores/internalValuationReviewStore";
import { chunkArray } from "../../../utils/utils";
import { ValueReconciliationBox } from "../valueReconciliationBox";
import { ValueReconciliationLayout, ValueReconciliationProbes } from "./valueReconciliationProbes";

const useStyles = makeStyles(() => ({
  page: {
    fontFamily: "Roboto, Arial, sans-serif",
    border: "1px solid lightgray",
    borderRadius: "5px",
    width: "1018px",
    height: "1440px",
    padding: "64px 40px 64px 40px",
  },
  //same page, but it only grows if its content turns out taller than a page (the pages are measured before this,
  //so it is just a safety net) instead of cutting the content off
  growingPage: {
    height: "auto",
    minHeight: "1440px",
  },
  hiddenStyle: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    opacity: 0,
    height: "450px",
    width: "100%",
  },
  mapContainer: {
    height: "450px",
    width: "100%",
  },
}));

interface Page3Props {
  internalValuationReviewStore: InternalValuationReviewStore;
}

function splitMarkers(markers) {
  const firstMarkersArray = markers.slice(0, 11);

  const remainingMarkersArrays =
    markers.length > 11
      ? chunkArray(markers.slice(11), 19)
      : [];

  return { firstMarkersArray, remainingMarkersArrays };
}

export const Page3 = ({ internalValuationReviewStore }: Page3Props) => {
  const classes = useStyles();
  const [reloadMap, setReloadMap] = useState(true);
  //null until the value reconciliation pages are measured
  const [layout, setLayout] = useState<ValueReconciliationLayout | null>(null);

  const markers = internalValuationReviewStore.getPropertyCompsMarkers();

  const valuationValues: any = internalValuationReviewStore.valuationFormStore.getFlattenedValues();

  const compsCount = (internalValuationReviewStore.propertyCompFormStores || []).length;

  const { firstMarkersArray, remainingMarkersArrays } = splitMarkers(markers);

  //the report is captured only when this is done
  useLayoutEffect(() => {
    internalValuationReviewStore.setReportLayoutPending(true);
    return () => internalValuationReviewStore.setReportLayoutPending(false);
  }, []);

  useEffect(() => {
    if (layout) {
      internalValuationReviewStore.setReportLayoutPending(false);
    }
  }, [layout]);

  useEffect(() => {
    const moveMapToRenderIt = async () => {
      setReloadMap(true);
      await new Promise((resolve) => setTimeout(resolve, 500));
      setReloadMap(false);
    };
    moveMapToRenderIt();
  }, []);

  const canShareMapPage = compsCount <= 4;
  const valueReconciliationInMapPage = canShareMapPage && !!layout?.inlineFits;
  const pagination = layout?.pagination;

  return (
    <>
      {!layout && (
        <ValueReconciliationProbes
          valuationValues={valuationValues}
          pageClassName={classes.page}
          canShareMapPage={canShareMapPage}
          showLegend={compsCount > 0}
          legendMarkers={firstMarkersArray}
          onMeasured={setLayout}
        />
      )}
      <div className={valueReconciliationInMapPage ? `${classes.page} ${classes.growingPage}` : classes.page}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <FieldSetBox title="Comparable">
              <Box
                className={reloadMap ? classes.hiddenStyle : classes.mapContainer}
              >
                <CompMap
                  markers={markers}
                />
              </Box>
            </FieldSetBox>
          </Grid>
          {compsCount > 0 && (
            <Grid item>
              <FieldSetBox title="Legend">
                <MapLegend markers={firstMarkersArray} />
              </FieldSetBox>
            </Grid>)
          }
          {valueReconciliationInMapPage && (
            <>
              <Grid item xs={12}>
                <ValueReconciliationBox valuationValues={valuationValues} />
              </Grid>
              <Grid item xs={12}>
                <StatementOfLimitingConditions />
              </Grid>
            </>)
          }
        </Grid>
      </div>
      {compsCount > 11 && (
        <>
          {remainingMarkersArrays.map((markers, index) => (
            <div key={`legend-${index}`} className={classes.page}>
              <Grid container direction="column" spacing={2}>
                <Grid item>
                  <FieldSetBox title="Legend">
                    <MapLegend markers={markers} />
                  </FieldSetBox>
                </Grid>
              </Grid>
            </div>
          ))}
        </>
      )}
      {/* The comments of the value reconciliation go page after page, the statement is always the last thing */}
      {pagination && pagination.chunks.map((chunk, index) => {
        const isFirst = index === 0;
        const isLast = index === pagination.chunks.length - 1;
        return (
          <div key={`value-reconciliation-${index}`} className={`${classes.page} ${classes.growingPage}`}>
            <Grid container direction="column" spacing={2}>
              <Grid item xs={12}>
                <ValueReconciliationBox
                  valuationValues={valuationValues}
                  comments={chunk}
                  title={isFirst ? "Value Reconciliation" : "Value Reconciliation (continued)"}
                  showValues={isFirst}
                />
              </Grid>
              {isLast && !pagination.statementOnItsOwnPage && (
                <Grid item xs={12}>
                  <StatementOfLimitingConditions />
                </Grid>
              )}
            </Grid>
          </div>
        );
      })}
      {pagination && pagination.statementOnItsOwnPage && (
        <div className={`${classes.page} ${classes.growingPage}`}>
          <Grid container direction="column" spacing={2}>
            <Grid item xs={12}>
              <StatementOfLimitingConditions />
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
};
